import React from "react";
import { BankModel } from "@/apidtos";
import { ACHForm, ACHFormValues } from "@phoenix/payments/components/ACHForm";
import { useApi } from "@core/api";
import { LoadingOverlay } from "@core/components/ui/loading-overlay";
import { Alert, AlertDescription } from "@core/components/ui/alert";
import { usePaymentContacts } from "@phoenix/payments/contexts/PaymentContactsContext";
import { ReplaceIfExtension } from "@core/components/PipelineComponents/ReplaceIfExtension";
import { useTranslation } from "react-i18next";

interface CreateACHModalProps {
  onSuccess: (paymentMethodId?: number) => void;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

/** Prefer RFC 7807 ProblemDetails.detail from axios-style API errors. */
function messageFromApiError(err: unknown, fallback: string): string {
  if (isRecord(err) && "response" in err && isRecord(err.response) && "data" in err.response) {
    const data = err.response.data;
    if (isRecord(data)) {
      if (typeof data.detail === "string" && data.detail.trim()) {
        return data.detail.trim();
      }
      if (typeof data.title === "string" && data.title.trim()) {
        return data.title.trim();
      }
    }
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return fallback;
}

export const CreateACHModal = (props: CreateACHModalProps) => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const api = useApi();
  const { billingContactId } = usePaymentContacts();
  const { t } = useTranslation();

  const create = async (data: ACHFormValues) => {
    const contactId = data.billingContactId ?? billingContactId;
    if (!contactId) {
      setErrorMsg(
        t("ui.billingContactRequired", "Must supply a billing contact to store a payment method")
      );
      return;
    }
    const model: BankModel = {
      AccountNumber: data.bankAccountNumber,
      RoutingNumber: data.bankRoutingNumber,
      Name: data.bankName,
      BillingContactId: contactId,
    };
    setErrorMsg(null);
    setLoading(true);
    try {
      const r = await api.AddCurrentUserACHPaymentMethod(data.name!, model);
      if (!r.IsSuccess) {
        throw new Error(r.Messages?.join(", ") || t("ui.AnUnknownErrorOccurred", "An unknown error occurred"));
      }
      props.onSuccess(r.Result);
    } catch (err) {
      console.error(err);
      setErrorMsg(
        messageFromApiError(
          err,
          t("ui.AnUnknownErrorOccurred", "An unknown error occurred")
        )
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      {loading && <LoadingOverlay />}
      {errorMsg && (
        <Alert variant="danger" className="mb-3">
          <AlertDescription>{errorMsg}</AlertDescription>
        </Alert>
      )}
      <ReplaceIfExtension name="payments.create-ach-modal" onSuccess={props.onSuccess}>
        <ACHForm
          includeName
          includeContactSelect={!billingContactId}
          onSubmit={create}
        />
      </ReplaceIfExtension>
    </div>
  );
};
